import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { Button } from './ui-kit';
import { createToken, updateToken } from '../api/tokens';
import { getPermissions, getUserPermissions } from '../api/permissions';
import { getUsers } from '../api/users';
import { useAuth } from '../context/AuthContext';
import PermissionsPicker from './PermissionsPicker';

const DEFAULT_CREATE_TTL = 365;

function TokenFormDialog({ open, onClose, token, onSuccess }) {
  const { user } = useAuth();
  const isEdit = !!token;

  const [formData, setFormData] = useState({
    name: '',
    owner_user_id: '',
    expires_in_days: '',
    description: '',
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setFormData({
        name: token.tokenName || '',
        owner_user_id: token.ownerUserId?._id || token.ownerUserId?.id || '',
        expires_in_days: '',
        description: token.description || '',
      });
      setSelectedPermissions(token.permissions || []);
    } else {
      setFormData({
        name: '',
        owner_user_id: user?.userId || '',
        expires_in_days: DEFAULT_CREATE_TTL,
        description: '',
      });
      setSelectedPermissions([]);
    }
  }, [open, isEdit, token, user?.userId]);

  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions', 'all'],
    queryFn: () => getPermissions({ all: true, isActive: true }),
    enabled: open,
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => getUsers(1, 1000, '', { all: true }),
    enabled: open,
  });

  const permissions = permissionsData?.permissions || [];
  const users = usersData?.users || [];

  const createMutation = useMutation({
    mutationFn: createToken,
    onSuccess: (data) => {
      toast.success('Token created successfully!');
      onSuccess(data.data, data.data.token);
    },
    onError: (error) => {
      toast.error(`Failed to create token: ${error.response?.data?.message || error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateToken(id, updates),
    onSuccess: () => {
      toast.success('Token updated successfully!');
      onSuccess();
    },
    onError: (error) => {
      toast.error(`Failed to update token: ${error.response?.data?.message || error.message}`);
    },
  });

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  const handleSyncFromOwner = async () => {
    if (!formData.owner_user_id) return;
    setIsSyncing(true);
    try {
      const ownerPermissions = await getUserPermissions(formData.owner_user_id);
      const matched = permissions.filter((p) =>
        ownerPermissions.some(
          (op) => op.service === p.service && op.resource === p.resource && op.action === p.action
        )
      );
      setSelectedPermissions(matched);
      if (matched.length === 0) {
        toast.info('Owner has no permissions to sync');
      } else {
        toast.success(`Synced ${matched.length} permission${matched.length !== 1 ? 's' : ''} from owner`);
      }
    } catch (error) {
      toast.error(`Failed to sync permissions: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Token name is required');
      return;
    }
    if (!formData.owner_user_id) {
      toast.error('Owner user is required');
      return;
    }

    const permissionIds = selectedPermissions.map((p) => p._id);

    if (isEdit) {
      updateMutation.mutate({
        id: token.id,
        updates: {
          name: formData.name,
          owner_user_id: formData.owner_user_id,
          permission_ids: permissionIds,
          description: formData.description,
        },
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        owner_user_id: formData.owner_user_id,
        expires_in_days: parseInt(formData.expires_in_days, 10) || DEFAULT_CREATE_TTL,
        description: formData.description,
        permission_ids: permissionIds,
      });
    }
  };

  const selectedOwner = users.find((u) => u.id === formData.owner_user_id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEdit ? 'Edit API Token' : 'Create API Token'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Token Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              placeholder="e.g., Production API Token"
            />

            <Autocomplete
              options={users}
              getOptionLabel={(option) => option?.fullName || option?.name || option?.email || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={selectedOwner || null}
              onChange={(e, newValue) => {
                setFormData({ ...formData, owner_user_id: newValue?.id || '' });
              }}
              loading={usersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Owner User"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {!isEdit && (
              <TextField
                label="Expires In (days)"
                type="number"
                value={formData.expires_in_days}
                onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                fullWidth
                inputProps={{ min: 1, max: 3650 }}
                helperText="Number of days until token expires (1-3650)"
              />
            )}

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this token"
            />

            <Divider sx={{ my: 1 }} />

            <PermissionsPicker
              availablePermissions={permissions}
              value={selectedPermissions}
              onChange={(next) => setSelectedPermissions(next)}
              loading={permissionsLoading}
              helperText="Optional — leave empty for services that don't require permissions"
              renderActions={() => (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!formData.owner_user_id || isSyncing}
                  onClick={handleSyncFromOwner}
                >
                  {isSyncing ? 'Syncing...' : "Sync from owner's permissions"}
                </Button>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting
              ? (isEdit ? 'Updating...' : 'Creating...')
              : (isEdit ? 'Update Token' : 'Create Token')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default TokenFormDialog;
